import type {
  TaskAssignmentResponse,
  TaskAssignmentSimpleResponse,
  TaskDefinitionResponse,
  TaskOfflineBundleResponse,
} from 'src/api/ProactApiClient';
import {
  cacheFilesForSession,
  getSessionCachedRefKeys,
} from 'src/offline/cachedFiles';
import type {CachedFileTarget} from 'src/offline/cachedFiles';
import type {HydrationPlanEntry} from 'src/offline/offlineSessionManager';
import {parseUploadFiles} from 'src/offline/pendingUploads';
import {store} from 'src/redux/store';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {getAssignmentStateMachine, getDefinitionStateMachine} from './api';
import {
  taskActionKey,
  taskAssignmentKey,
  tasksAssignmentsListKey,
  tasksAssignmentStateMachineKey,
  tasksComplianceStandardsKey,
  tasksDefinitionStateMachineKey,
  tasksPersonnelKey,
  tasksProjectsKey,
} from './snapshotKeys';

const ESTIMATED_ASSIGNMENT_BYTES = 20_000;
const ESTIMATED_PERSONNEL_ROW_BYTES = 100;
const ESTIMATED_OPTION_BYTES = 60;
// Inline attachments carry no size; estimate for the pre-fetch quota guard.
const ESTIMATED_ATTACHMENT_BYTES = 2_000_000;

/** Cached list row: the simple shape plus the scope id the offline reads filter on. */
export type CachedAssignmentRow = TaskAssignmentSimpleResponse & {
  taskDefinition: TaskAssignmentSimpleResponse['taskDefinition'] & {
    projectId?: number;
  };
};

/** The list row shape the my-tasks lists read, derived from the detail row. */
export const toAssignmentListRow = (
  assignment: TaskAssignmentResponse
): CachedAssignmentRow =>
  ({
    ...assignment,
    dueAt: assignment.taskDefinition?.dueAt,
    taskDefinition: {
      ...assignment.taskDefinition,
      projectId:
        assignment.taskDefinition?.projectId ??
        assignment.taskDefinition?.project?.id,
    },
  }) as unknown as CachedAssignmentRow;

/** Every inline attachment and Content PDF of the bundled definitions, deduped by storage key. */
export const collectInlineAttachments = (
  bundle: TaskOfflineBundleResponse
): CachedFileTarget[] => {
  const definitions: TaskDefinitionResponse[] = [
    ...bundle.assignments.map(assignment => assignment.taskDefinition),
    ...bundle.actions.map(action => action.definition),
  ];
  const targets = new Map<string, CachedFileTarget>();
  const token = store.getState().auth.accessToken;
  definitions.forEach(definition => {
    [
      ...(parseUploadFiles(definition?.metadata?.inlineAttachments) ?? []),
      ...(parseUploadFiles(definition?.contentPdfUrl) ?? []),
    ]
      .filter(file => file.success && file.value)
      .forEach(file => {
        const refKey = file.encodedKey || file.value;
        if (targets.has(refKey)) return;
        targets.set(refKey, {
          refKey,
          name: file.name,
          url: getUrlWithEncodingAndAuth(file.value, token),
        });
      });
  });
  return [...targets.values()];
};

export const estimateBundleBytes = (bundle: TaskOfflineBundleResponse): number =>
  (bundle.assignments.length + bundle.actions.length) *
    ESTIMATED_ASSIGNMENT_BYTES +
  (bundle.personnel?.rows.length ?? 0) * ESTIMATED_PERSONNEL_ROW_BYTES +
  (bundle.complianceStandards.length + bundle.projects.length) *
    ESTIMATED_OPTION_BYTES +
  collectInlineAttachments(bundle).length * ESTIMATED_ATTACHMENT_BYTES;

// Snapshot plan for the tasks session: the list, one row per assignment and
// Action, both state machines, and the three reference sections.
export const buildTasksHydrationPlan = (
  bundle: TaskOfflineBundleResponse
): HydrationPlanEntry[] => {
  const personnel = bundle.personnel;
  return [
    {
      key: tasksAssignmentsListKey,
      run: async () => bundle.assignments.map(toAssignmentListRow),
    },
    ...bundle.assignments.map(assignment => ({
      key: taskAssignmentKey(assignment.id),
      run: async () => assignment,
    })),
    ...bundle.actions.map(action => ({
      key: taskActionKey(action.definition.id),
      run: async () => action,
    })),
    {key: tasksAssignmentStateMachineKey, run: () => getAssignmentStateMachine()},
    {key: tasksDefinitionStateMachineKey, run: () => getDefinitionStateMachine()},
    ...(personnel
      ? [{key: tasksPersonnelKey, run: async () => personnel}]
      : []),
    {
      key: tasksComplianceStandardsKey,
      run: async () => bundle.complianceStandards,
    },
    {key: tasksProjectsKey, run: async () => bundle.projects},
  ];
};

// Downloads inline attachments as cached blobs so a Toolbox Talk document opens
// offline; already-cached keys are skipped so a refresh resumes cheaply.
export const cacheTaskInlineAttachments = async (
  sessionId: string,
  bundle: TaskOfflineBundleResponse
): Promise<void> => {
  const cached = await getSessionCachedRefKeys(sessionId);
  const targets = collectInlineAttachments(bundle).filter(
    target => !cached.has(target.refKey)
  );
  if (targets.length === 0) return;
  await cacheFilesForSession({sessionId, targets});
};
